import { useCallback, useEffect, useState } from "react";
import { Users, Plus, Building, Mail, MoreVertical, PauseCircle, PlayCircle, Send, Trash2, MessageSquare } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/pages/Dashboard";
import ClientDetail from "@/pages/ClientDetail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS_STYLES = {
  active: "bg-emerald-500/15 text-emerald-400",
  invited: "bg-amber-500/15 text-amber-400",
  suspended: "bg-red-500/15 text-red-400",
};
const EMPTY = { name: "", email: "", company: "", business_type: "" };

function ClientCard({ client, onOpen, onSuspend, onReactivate, onResend, onRemove }) {
  const [menu, setMenu] = useState(false);
  return (
    <div className="fv-card-hover rounded-2xl border border-white/8 bg-[#121214] p-5 relative" data-testid="admin-client-card">
      <div className="flex items-start justify-between mb-4">
        <div className="w-11 h-11 rounded-full fv-gold-gradient flex items-center justify-center text-[#0A0A0B] font-semibold">{client.name?.[0]?.toUpperCase()}</div>
        <div className="flex items-center gap-2">
          {client.unread_from_client > 0 && <span className="flex items-center gap-1 text-xs text-[#D4AF37]"><MessageSquare className="w-3.5 h-3.5" />{client.unread_from_client}</span>}
          <span className={`text-xs px-2.5 py-1 rounded-full capitalize ${STATUS_STYLES[client.status] || STATUS_STYLES.invited}`}>{client.status}</span>
          <button onClick={() => setMenu((v) => !v)} data-testid={`admin-client-menu-${client.client_id}`} className="text-[#71717A] hover:text-white"><MoreVertical className="w-4 h-4" /></button>
        </div>
      </div>
      {menu && (
        <div className="absolute right-4 top-14 z-10 w-44 rounded-xl border border-white/10 bg-[#1C1C1F] py-1.5 shadow-xl" onMouseLeave={() => setMenu(false)}>
          {client.status !== "suspended"
            ? <button onClick={() => { onSuspend(client); setMenu(false); }} className="w-full flex items-center gap-2 px-3.5 py-2 text-sm text-[#D4D4D8] hover:bg-white/5"><PauseCircle className="w-4 h-4" /> Suspend</button>
            : <button onClick={() => { onReactivate(client); setMenu(false); }} className="w-full flex items-center gap-2 px-3.5 py-2 text-sm text-[#D4D4D8] hover:bg-white/5"><PlayCircle className="w-4 h-4" /> Reactivate</button>}
          <button onClick={() => { onResend(client); setMenu(false); }} className="w-full flex items-center gap-2 px-3.5 py-2 text-sm text-[#D4D4D8] hover:bg-white/5"><Send className="w-4 h-4" /> Resend invite</button>
          <button onClick={() => { onRemove(client); setMenu(false); }} data-testid={`admin-client-delete-${client.client_id}`} className="w-full flex items-center gap-2 px-3.5 py-2 text-sm text-red-400 hover:bg-white/5"><Trash2 className="w-4 h-4" /> Remove</button>
        </div>
      )}
      <button onClick={() => onOpen(client)} data-testid={`admin-open-client-${client.client_id}`} className="text-left w-full">
        <div className="text-white font-medium">{client.name}</div>
        {client.company && <div className="flex items-center gap-1.5 text-xs text-[#71717A] mt-1"><Building className="w-3.5 h-3.5" /> {client.company}</div>}
        <div className="flex items-center gap-1.5 text-xs text-[#71717A] mt-1"><Mail className="w-3.5 h-3.5" /> {client.email}</div>
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-[#71717A] mb-1"><span>Progress</span><span>{client.progress_pct}%</span></div>
          <div className="h-1.5 rounded-full bg-white/8 overflow-hidden"><div className="h-full fv-gold-gradient" style={{ width: `${client.progress_pct}%` }} /></div>
        </div>
      </button>
    </div>
  );
}

export default function ClientPortal() {
  const [clients, setClients] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState(null);

  const load = useCallback(() => api.get("/portal-admin/clients").then(({ data }) => setClients(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post("/portal-admin/clients", form); toast.success("Client invited — activation email sent"); setCreateOpen(false); setForm(EMPTY); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const suspend = async (c) => { try { await api.post(`/portal-admin/clients/${c.client_id}/suspend`); toast.success("Client suspended"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const reactivate = async (c) => { try { await api.post(`/portal-admin/clients/${c.client_id}/reactivate`); toast.success("Client reactivated"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const resend = async (c) => { try { await api.post(`/portal-admin/clients/${c.client_id}/resend-invite`); toast.success("Invitation resent"); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name}'s account and all their data? This cannot be undone.`)) return;
    setClients((cs) => cs.filter((x) => x.client_id !== c.client_id));
    try { await api.delete(`/portal-admin/clients/${c.client_id}`); toast.success("Client removed"); } catch { load(); }
  };

  return (
    <div className="fv-fade-up">
      <div className="flex items-start justify-between gap-4 mb-8">
        <PageHeader eyebrow="Grow" title="Client Portal" subtitle="Create client accounts and manage each client's private workspace — progress, messages, documents, and billing." />
        <Button onClick={() => setCreateOpen(true)} data-testid="add-client-btn" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full hover:opacity-90 shrink-0">
          <Plus className="w-4 h-4 mr-1" /> New client
        </Button>
      </div>

      {clients.length === 0 ? (
        <div className="text-center py-20 rounded-2xl border border-dashed border-white/10 bg-[#0f0f11]" data-testid="clients-empty">
          <Users className="w-10 h-10 text-[#52525B] mx-auto mb-3" />
          <p className="text-[#71717A]">No client accounts yet. Invite your first client to their portal.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-grid">
          {clients.map((c) => <ClientCard key={c.client_id} client={c} onOpen={setActive} onSuspend={suspend} onReactivate={reactivate} onResend={resend} onRemove={remove} />)}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Invite a client</DialogTitle>
            <DialogDescription className="text-[#71717A]">They'll get an email to activate their private portal.</DialogDescription></DialogHeader>
          <form onSubmit={create} className="space-y-4" data-testid="client-form">
            <div><Label className="text-[#D4D4D8] mb-1.5 block text-sm">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="client-name-input" className="bg-[#0d0d0f] border-white/10 text-white" /></div>
            <div><Label className="text-[#D4D4D8] mb-1.5 block text-sm">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="client-email-input" className="bg-[#0d0d0f] border-white/10 text-white" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[#D4D4D8] mb-1.5 block text-sm">Company</Label>
                <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="client-company-input" className="bg-[#0d0d0f] border-white/10 text-white" /></div>
              <div><Label className="text-[#D4D4D8] mb-1.5 block text-sm">Business type</Label>
                <Input value={form.business_type} onChange={(e) => setForm({ ...form, business_type: e.target.value })} data-testid="client-type-input" className="bg-[#0d0d0f] border-white/10 text-white" /></div>
            </div>
            <Button type="submit" disabled={saving} data-testid="save-client-btn" className="w-full fv-gold-gradient text-[#0A0A0B] font-semibold rounded-lg hover:opacity-90">
              {saving ? "Sending invite…" : "Send invitation"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{active?.name}</DialogTitle>
            <DialogDescription className="text-[#71717A]">{active?.company || active?.email} · {active?.status}</DialogDescription></DialogHeader>
          {active && <ClientDetail clientId={active.client_id} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
